export type TvFill = 'blur' | 'black';
export type TvOrder = 'event' | 'random' | 'chrono';

export interface TvConfig {
  fill: TvFill;
  showCaption: boolean;
  order: TvOrder;
  dwellMs: number;
  idleMs: number;
}

type TvConfigResult =
  | { ok: true; config: TvConfig }
  | { ok: false; errors: string[] };

const TV_FILLS: readonly TvFill[] = ['blur', 'black'];
const TV_ORDERS: readonly TvOrder[] = ['event', 'random', 'chrono'];
const CAPTION_VALUES = ['on', 'off'] as const;

const DEFAULT_FILL: TvFill = 'blur';
const DEFAULT_CAPTION = CAPTION_VALUES[0];
const DEFAULT_ORDER: TvOrder = 'event';
const DEFAULT_DWELL_SECONDS = 30;
const DWELL_SECONDS_RANGE = { min: 15, max: 120 } as const;   // the floor is a spend guard, not a taste bound
const DEFAULT_IDLE_HOURS = 6;
// The ceiling is the spend guard; the floor only bounds how early the TV parks,
// and a low floor is what makes park behavior verifiable in one sitting.
const IDLE_HOURS_RANGE = { min: 0.05, max: 12 } as const;

const MS_PER_SECOND = 1_000;
const MS_PER_HOUR = 3_600_000;

interface NumberRange { min: number; max: number }

const TV_PARAM_NAMES = ['fill', 'caption', 'order', 'dwell', 'idleHours'] as const;

function readChoice<T extends string>(
  params: URLSearchParams,
  name: string,
  choices: readonly T[],
  fallback: T,
  errors: string[]
): T {
  const raw = params.get(name);
  if (raw === null) {
    return fallback;
  }

  if (choices.includes(raw as T)) {
    return raw as T;
  }

  errors.push(`${name}=${raw} (allowed: ${choices.join(', ')})`);
  return fallback;
}

function readNumber(
  params: URLSearchParams,
  name: string,
  range: NumberRange,
  fallback: number,
  errors: string[]
): number {
  const raw = params.get(name);
  if (raw === null) {
    return fallback;
  }

  const value = Number(raw);
  if (Number.isFinite(value) && value >= range.min && value <= range.max) {
    return value;
  }

  errors.push(`${name}=${raw} (allowed: ${range.min}–${range.max})`);
  return fallback;
}

// A misspelled key reads exactly like an omitted one, so it would silently play at the
// default the author was trying to override. Reported before the values so the frame
// names the key that does not exist rather than the range it never reached.
function readUnknownNames(params: URLSearchParams, errors: string[]): void {
  const known: readonly string[] = TV_PARAM_NAMES;
  new Set(params.keys()).forEach((name) => {
    if (!known.includes(name)) {
      errors.push(`${name} (unknown param; allowed: ${TV_PARAM_NAMES.join(', ')})`);
    }
  });
}

// The URL is hand-authored, so an unknown or out-of-range value is a typo: report every
// bad param and let the caller refuse to play rather than silently running at a default.
export function parseTvConfig(search: string): TvConfigResult {
  const params = new URLSearchParams(search);
  const errors: string[] = [];
  readUnknownNames(params, errors);

  // Each read appends its own error, so the reads run in the order the frame lists them.
  const fill = readChoice(params, 'fill', TV_FILLS, DEFAULT_FILL, errors);
  const caption = readChoice(params, 'caption', CAPTION_VALUES, DEFAULT_CAPTION, errors);
  const order = readChoice(params, 'order', TV_ORDERS, DEFAULT_ORDER, errors);
  const dwellSeconds = readNumber(params, 'dwell', DWELL_SECONDS_RANGE, DEFAULT_DWELL_SECONDS, errors);
  const idleHours = readNumber(params, 'idleHours', IDLE_HOURS_RANGE, DEFAULT_IDLE_HOURS, errors);

  if (errors.length) {
    return { ok: false, errors };
  }

  const config: TvConfig = {
    fill,
    showCaption: caption === 'on',
    order,
    dwellMs: dwellSeconds * MS_PER_SECOND,
    idleMs: idleHours * MS_PER_HOUR,
  };

  return { ok: true, config };
}
